import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Mesh, OGLRenderingContext, Program, Renderer, Texture, Triangle, Vec2 } from 'ogl';

import { GRAIN_PULSE_DURATION_MS, GrainPulseService } from '../../services/grain-pulse.service';
import { fragment, vertex } from './shaders';

const DEFAULT_PEAK_OPACITY = 0.5;

@Component({
  selector: 'app-grain-pulse',
  templateUrl: './grain-pulse.component.html',
  styleUrl: './grain-pulse.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GrainPulseComponent implements AfterViewInit, OnDestroy {
  /** Total runtime of one pulse. Defaults to the wizard's 700ms — the
   *  auth-transition overlay overrides this to ~1100ms for a longer
   *  filmic sweep. */
  @Input() durationMs = GRAIN_PULSE_DURATION_MS;

  /** Peak opacity reached at t=0.5 of the sin envelope. Defaults to 0.5
   *  (subtle background pulse). Auth-transition uses 0.65 so the wave
   *  fully masks the navigation transition. */
  @Input() peakOpacity = DEFAULT_PEAK_OPACITY;

  /** When true (default) the component auto-subscribes to the global
   *  `GrainPulseService` so wizard step-changes can trigger it. The
   *  auth-transition overlay disables this and drives the pulse via
   *  its imperative `pulse()` API instead, otherwise every wizard
   *  step would also fire the fullscreen login overlay. */
  @Input() listenToService = true;

  @ViewChild('glCanvas', { static: true })
  private canvasRef!: ElementRef<HTMLCanvasElement>;

  private renderer: Renderer | null = null;
  private program: Program | null = null;
  private mesh: Mesh | null = null;
  private resolutionUniform: Vec2 | null = null;

  private rafId: number | null = null;
  private pulseStart = 0;
  private animating = false;
  /** Active duration / peak for the in-flight pulse. Captured at start
   *  so mid-flight input changes don't warp an animation that's already
   *  running. */
  private activeDurationMs = this.durationMs;
  private activePeakOpacity = this.peakOpacity;

  private resizeObserver: ResizeObserver | null = null;
  private subscription: Subscription | null = null;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    private readonly grainPulse: GrainPulseService,
  ) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.initWebGL();
      if (this.listenToService) {
        this.subscription = this.grainPulse.pulse$.subscribe(() =>
          this.startPulse(),
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    const gl = this.renderer?.gl;
    if (gl) {
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
    this.renderer = null;
    this.program = null;
    this.mesh = null;
  }

  /** Imperative trigger — used by AuthTransitionComponent to fire the
   *  pulse on demand with custom timing. Optional overrides win over
   *  the input bindings for this single run. */
  pulse(overrides?: { durationMs?: number; peakOpacity?: number }): void {
    if (overrides?.durationMs !== undefined) {
      this.activeDurationMs = overrides.durationMs;
    } else {
      this.activeDurationMs = this.durationMs;
    }
    if (overrides?.peakOpacity !== undefined) {
      this.activePeakOpacity = overrides.peakOpacity;
    } else {
      this.activePeakOpacity = this.peakOpacity;
    }
    this.startPulse();
  }

  private initWebGL(): void {
    const canvas = this.canvasRef.nativeElement;

    let renderer: Renderer;
    try {
      renderer = new Renderer({
        canvas,
        alpha: true,
        premultipliedAlpha: false,
        dpr: Math.min(window.devicePixelRatio || 1, 2),
      });
    } catch {
      // WebGL unavailable — fail silently, host stays invisible.
      return;
    }

    this.renderer = renderer;
    const gl = renderer.gl;

    const geometry = new Triangle(gl);
    const gradient = this.createGradientTexture(gl);
    this.resolutionUniform = new Vec2(1, 1);

    this.program = new Program(gl, {
      vertex,
      fragment,
      uniforms: {
        uTime: { value: 0 },
        uGradient: { value: gradient },
        uResolution: { value: this.resolutionUniform },
      },
      transparent: true,
    });

    this.mesh = new Mesh(gl, { geometry, program: this.program });

    this.applySize();
    this.observeResize();
  }

  private observeResize(): void {
    const target = this.host.nativeElement;
    this.resizeObserver = new ResizeObserver(() => this.applySize());
    this.resizeObserver.observe(target);
  }

  private applySize(): void {
    if (!this.renderer || !this.resolutionUniform) return;
    const rect = this.host.nativeElement.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    this.renderer.setSize(width, height);
    this.resolutionUniform.set(width, height);
  }

  private createGradientTexture(gl: OGLRenderingContext): Texture {
    const width = 256;
    const offscreen = document.createElement('canvas');
    offscreen.width = width;
    offscreen.height = 1;
    const ctx = offscreen.getContext('2d');
    if (ctx) {
      // Two-tone palette — brand-blue ↔ soft lavender. Loops back to blue
      // at 100% so the noise field doesn't show a visible seam where the
      // gradient wraps. Kept narrow on purpose: the previous five-stop
      // rainbow read as too loud against the calm wizard surface.
      const stops: Array<[number, string]> = [
        [0.0, '#3b82f6'],
        [0.5, '#b47fff'],
        [1.0, '#3b82f6'],
      ];
      const grad = ctx.createLinearGradient(0, 0, width, 0);
      stops.forEach(([stop, color]) => grad.addColorStop(stop, color));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, 1);
    }
    return new Texture(gl, {
      image: offscreen,
      generateMipmaps: false,
      width,
      height: 1,
    });
  }

  private startPulse(): void {
    if (!this.renderer || !this.program || !this.mesh) return;
    this.pulseStart = performance.now();
    if (!this.animating) {
      this.animating = true;
      this.tick();
    }
  }

  private tick = (): void => {
    if (!this.renderer || !this.program || !this.mesh) {
      this.animating = false;
      return;
    }

    const elapsed = performance.now() - this.pulseStart;
    const t = Math.min(elapsed / this.activeDurationMs, 1);

    // Sweep the noise field forward through z-space during the pulse.
    this.program.uniforms['uTime'].value = t * 1.6;

    // Sin envelope: 0 → 1 (peak at t=0.5) → 0 — soft fade in & out.
    const envelope = Math.sin(t * Math.PI);
    this.canvasRef.nativeElement.style.opacity = String(
      envelope * this.activePeakOpacity,
    );

    this.renderer.render({ scene: this.mesh });

    if (t < 1) {
      this.rafId = requestAnimationFrame(this.tick);
    } else {
      this.animating = false;
      this.canvasRef.nativeElement.style.opacity = '0';
      this.rafId = null;
    }
  };
}
